/**
 * Saturday release-date helpers for magazine previous issues.
 * Magazines publish every Saturday; previous slots are labeled and
 * named by that date so admins are not confused by Issue N vs weekN.
 */

export const DEFAULT_PREVIOUS_COVER_URL =
  'https://dp9h3awfnhccl.cloudfront.net/magazines/default_image.jpeg';

const pad2 = (n) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD */
export const toIsoDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Parse YYYY-MM-DD as a local Date at midnight */
export const parseIsoDate = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const d = new Date(year, month, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Most recent Saturday strictly before `fromDate`.
 * If `fromDate` is Saturday, returns the previous week (current issue day).
 */
export const getSaturdayBefore = (fromDate = new Date()) => {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const daysSinceSaturday = day === 6 ? 7 : (day + 1) % 7;
  return addDays(d, -daysSinceSaturday);
};

/** How many weekly previous slots to seed from when starting empty */
export const INITIAL_PREVIOUS_WEEKS = 8;

/**
 * Oldest Saturday in a fresh previous list (N weeks old vs current week).
 * With INITIAL_PREVIOUS_WEEKS=8: Saturday before today, minus 7 more weeks.
 */
export const getInitialPreviousReleaseDate = (fromDate = new Date()) => {
  const oneWeekOld = getSaturdayBefore(fromDate);
  return addDays(oneWeekOld, -7 * (INITIAL_PREVIOUS_WEEKS - 1));
};

/**
 * Release date for a newly added previous issue (appended = newest).
 * Empty list → 8 weeks ago (start of last-8 window). Otherwise → newest + 7 days.
 */
export const getNextPreviousReleaseDate = (previousIssues = []) => {
  if (!previousIssues.length) {
    return toIsoDate(getInitialPreviousReleaseDate(new Date()));
  }

  const newest = previousIssues[previousIssues.length - 1];
  const newestIso = newest?.releaseDate || null;
  const newestDate = parseIsoDate(newestIso);

  if (newestDate) {
    return toIsoDate(addDays(newestDate, 7));
  }

  // Existing rows without releaseDate: treat newest as Saturday-before-today,
  // so the new row is that date + 7 days (aligned with current week).
  return toIsoDate(addDays(getSaturdayBefore(new Date()), 7));
};

/**
 * Derive a Saturday ISO date for an existing previous issue that has no
 * releaseDate stored. Assumes weekly Saturdays, newest (last index) =
 * Saturday before today, each older index one week earlier.
 */
export const deriveReleaseDateForIndex = (actualIdx, previousLength, fromDate = new Date()) => {
  if (previousLength <= 0) return toIsoDate(getSaturdayBefore(fromDate));
  const weeksBackFromNewest = previousLength - 1 - actualIdx;
  const newestSaturday = getSaturdayBefore(fromDate);
  return toIsoDate(addDays(newestSaturday, -7 * weeksBackFromNewest));
};

export const resolveReleaseDate = (issue, actualIdx, previousLength) => {
  if (issue?.releaseDate && parseIsoDate(issue.releaseDate)) {
    return issue.releaseDate;
  }
  return deriveReleaseDateForIndex(actualIdx, previousLength);
};

/** e.g. "Sat 2 Aug 2026" */
export const formatSaturdayLabel = (iso) => {
  const d = parseIsoDate(iso);
  if (!d) return 'Previous issue';
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export const suggestedCoverFilename = (iso, level) => {
  const safeIso = parseIsoDate(iso) ? iso : toIsoDate(getSaturdayBefore(new Date()));
  return `${safeIso}_${level}.jpeg`;
};
